import { useEffect } from 'react'

const Heritage = () => {
  useEffect(() => {
    document.title = "Why This Matters - Haight & Ashbury"
  }, [])

  return (
    <div>
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-8">
              Why This Matters
            </h1>
            <p className="text-[20px] leading-[26px] tracking-[-0.02em] text-haight-gold">
              The corner of Haight and Ashbury is more than a geographic intersection—it's a symbol of a global cultural revolution.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto space-y-16">
            {/* Why This Matters Section */}
            <div className="space-y-6">
              <h2 className="text-haight-navy">
                Why This Matters
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  The corner of Haight and Ashbury is more than a geographic intersection—it's a symbol of a global cultural revolution. Since its founding, San Francisco has been a beacon of "counterculture" and innovation. As the spiritual epicenter of the 1960s, the Haight Ashbury neighborhood fueled revolutions in music, art, civil rights, LGBTQ+ identity, environmentalism, and technology. Today, the Doolan-Larson buildings stand at the heart of that legacy. The question, "What comes next?," begs to be answered.
                </p>
                <p>
                  Reimagining this site is a rare opportunity to transform a landmark into a living cultural catalyst. It is an opportunity to shape how the stories that are rooted here will continue to evolve. It is an opportunity to use the power of place to engage and inspire.
                </p>
                <p>
                  The buildings themselves are expressions of how a city rebuilds, adapts, and reinvents itself. From the aftermath of the 1906 earthquake to the arrival of a youth-led counterculture, this site has long drawn those seeking new ways forward. Preserving that energy means honoring history while embracing new ideas.
                </p>
                <p>
                  San Francisco Heritage sees this project as a model for adaptive reuse —demonstrating how historic preservation has an essential place in contemporary culture and civic life.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg">
                <p className="text-haight-navy">
                  To understand more about San Francisco Heritage and its mission, please visit:{' '}
                  <a 
                    href="https://sfheritage.org" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-haight-gold hover:text-haight-brown underline transition-colors duration-200"
                  >
                    sfheritage.org
                  </a>
                </p>
              </div>
            </div>

            {/* Haight Ashbury Section */}
            <div className="space-y-6">
              <h2 className="text-haight-navy">
                Haight Ashbury
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  Haight Ashbury is one of San Francisco's most intact Victorian neighborhoods. All but synonymous with "Counterculture" and "Summer of Love," the district is a magnet for both tourists and San Franciscans searching for remnants of its colorful, infamous, and powerful past.
                </p>
                <p>
                  Given its relative isolation, proximity to parks, and cheap housing, the neighborhood was attractive to the young and itinerant, first the Beatniks in the late 1950s and then the Hippies in the 1960s. A strong interest in the spiritual and creative, individualism, psychedelics, and the emerging anti-establishment movements was swirling throughout the district and shaping its character.
                </p>
              </div>
            </div>

            {/* Doolan-Larson House Section */}
            <div className="space-y-6">
              <h2 className="text-haight-navy">
                The Doolan-Larson House & Storefronts
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  Built in 1903, the Colonial Revival Doolan-Larson House is located at 557 Ashbury Street, on the northwest corner of Haight and Ashbury streets. After the 1906 Earthquake and Fire, the building was elevated to accommodate the addition of six commercial storefronts along Haight Street. The ensemble comprises approximately 7,500 square feet.
                </p>
                <p>
                  Since 1907, many businesses have occupied the storefronts, telling a story of a dynamic San Francisco neighborhood: a saloon, jewelry store, optometrist, cigar store, shoeshine shop, dry cleaner, plumber, realtor, accountant, barbershop, women's clothing store, beauty shop, insurance agent, hatter, and magazine shop. In 1907, 1508 Haight was a barbershop, and it is still a barbershop today.
                </p>
                <p>
                  In 1965, Peggy Caserta opened Mnasidika at 1510 Haight, the first "Hippie" shop on Haight and home of the original bell-bottoms.
                </p>
                <p>
                  The upper floors of the building were originally the residence of Richard P. Doolan, who retained the property until his death in 1947, and it remained in his family until 1973. In 1918, the building was subdivided and became the Evelyn Apartments. The name "Evelyn" is still on the transom window above the entrance door on the east elevation.
                </p>
                <p>
                  Norman Tyler Larson, a fourth-generation San Franciscan and Log Cabin Republican, bought the building in 1980 and spent years restoring it.
                </p>
                
                <blockquote className="border-l-4 border-haight-gold pl-6 italic text-lg text-gray-600">
                  The Haight-Ashbury was at its low point, in the aftermath of the '60s, and prices were very low. My only connection with The Haight at that point was that I liked Janis Joplin. Soon after buying that first building, I did grow a beard, and have had one ever since.
                  <footer className="mt-2 text-base font-normal text-haight-navy">– Norm Larson</footer>
                </blockquote>
                
                <p>
                  Larson engaged with the local businesses and people on the street, hosted events and legendary parties, and before long he was known as the "Duke of Haight".
                </p>
                
                <blockquote className="border-l-4 border-haight-gold pl-6 italic text-lg text-gray-600">
                  Norm was very hospitable and proud of his home. He had researched it and could talk about what happened in each room 80 years earlier. He'd rattle off names of old politicians. He knew them – we didn't…It's fun to be in such an old place…We had events there two to four times a year: chapter meetings, Christmas parties, guest speakers. It was a go-to place. If those people at the Summer of Love had known that same place was filled with gay Republicans it'd shock them.
                  <footer className="mt-2 text-base font-normal text-haight-navy">– Fred Schein, former president of the San Francisco chapter of the Log Cabin Republicans.</footer>
                </blockquote>
                
                <p>
                  In 2006, Larson's home was designated San Francisco Landmark #253, and in 2011 it was listed in the National Register of Historic Places. Before his death in 2018, Larson donated the property to San Francisco Heritage so that it would be preserved for public use, enrichment, and enjoyment.
                </p>
                
                <blockquote className="border-l-4 border-haight-gold pl-6 italic text-lg text-gray-600">
                  When you entered the house, you entered a new world," Schein recalled. "Quite dramatic, quite antique, heavy wood, a lot of redwood. We always had a lot of fun going up and down the staircase. Norm had a dramatic painting of himself that confronted you, with his grand, white hair and white beard looking like Colonel Sanders.
                  <footer className="mt-2 text-base font-normal text-haight-navy">– Schein</footer>
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Heritage


